import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAdminUsers, useBulkUserAction } from '../useAdmin';
import type { AdminUserRole, BulkUserAction, BulkUserResult, ListUsersParams } from '../adminApi';
import { AuthApiError } from '../../account/authApi';
import './AdminUsers.css';

const PAGE_SIZE = 25;

const ROLE_OPTIONS: { value: '' | AdminUserRole; label: string }[] = [
  { value: '', label: 'Any role' },
  { value: 'standard', label: 'Standard' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'moderator', label: 'Moderator' },
  { value: 'admin', label: 'Admin' },
];

const SUSPENDED_OPTIONS: { value: '' | 'true' | 'false'; label: string }[] = [
  { value: '', label: 'Active + suspended' },
  { value: 'false', label: 'Active only' },
  { value: 'true', label: 'Suspended only' },
];

/**
 * Admin user list (F3). Free-text email search + role filter +
 * suspended filter + offset pagination. Each row links to the
 * detail page (F4) for the per-user actions.
 */
export function AdminUsers(): JSX.Element {
  const [searchParams] = useSearchParams();
  const initialSuspended = (searchParams.get('suspended') as 'true' | 'false' | null) ?? '';
  const [q, setQ] = useState('');
  const [role, setRole] = useState<'' | AdminUserRole>('');
  const [suspended, setSuspended] = useState<'' | 'true' | 'false'>(initialSuspended);
  const [page, setPage] = useState(0);

  // When the URL ?suspended= param flips (e.g. clicking the
  // "Suspended" sidebar entry), sync the dropdown.
  useEffect(() => {
    const fromUrl = (searchParams.get('suspended') as 'true' | 'false' | null) ?? '';
    if (fromUrl !== suspended) {
      setSuspended(fromUrl);
      setPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkResult, setBulkResult] = useState<BulkUserResult | null>(null);
  const bulk = useBulkUserAction();

  const params = useMemo<ListUsersParams>(
    () => ({
      q: q.trim() || undefined,
      role: role || undefined,
      suspended: suspended === 'true' ? true : suspended === 'false' ? false : undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [q, role, suspended, page],
  );

  const { data, isLoading, isError, error, isFetching } = useAdminUsers(params);

  const total = data?.total ?? 0;
  const showing = data?.rows.length ?? 0;
  const startIndex = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const endIndex = page * PAGE_SIZE + showing;
  const canPrev = page > 0;
  const canNext = (page + 1) * PAGE_SIZE < total;

  function resetToFirstPage<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(0);
      setSelected(new Set());
    };
  }

  const visibleIds = data?.rows.map((r) => r.id) ?? [];
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSelected = visibleIds.some((id) => selected.has(id));

  function toggleAllVisible() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        for (const id of visibleIds) next.delete(id);
      } else {
        for (const id of visibleIds) next.add(id);
      }
      return next;
    });
  }

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function runBulk(action: BulkUserAction): Promise<void> {
    if (selected.size === 0) return;
    let reason: string | undefined;
    if (action === 'suspend') {
      const r = window.prompt(`Reason for suspending ${selected.size} user(s)?`);
      if (!r || !r.trim()) return;
      reason = r.trim();
    } else if (action === 'delete') {
      const ok = window.confirm(
        `Permanently delete ${selected.size} user(s)? This cascades to their projects, samples, submissions, and published apps.`,
      );
      if (!ok) return;
    }
    const result = await bulk.mutateAsync({
      user_ids: Array.from(selected),
      action,
      ...(reason ? { reason } : {}),
    });
    setBulkResult(result);
    setSelected(new Set());
  }

  return (
    <section className="bl-admin-users" aria-label="User management">
      <header className="bl-admin-users__header">
        <h2>Users</h2>
        <p className="bl-admin-users__count" aria-live="polite">
          {isLoading
            ? 'Loading…'
            : total === 0
              ? 'No users match'
              : `${startIndex.toLocaleString()}–${endIndex.toLocaleString()} of ${total.toLocaleString()}`}
          {isFetching && !isLoading && ' · refreshing…'}
        </p>
      </header>

      <form
        className="bl-admin-users__filters"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(0);
        }}
        role="search"
      >
        <label className="bl-admin-users__field">
          <span className="bl-admin-users__field-label">Email contains</span>
          <input
            type="search"
            value={q}
            placeholder="ada@school.edu"
            onChange={(e) => resetToFirstPage(setQ)(e.target.value)}
            autoComplete="off"
          />
        </label>
        <label className="bl-admin-users__field">
          <span className="bl-admin-users__field-label">Role</span>
          <select
            value={role}
            onChange={(e) => resetToFirstPage(setRole)(e.target.value as '' | AdminUserRole)}
          >
            {ROLE_OPTIONS.map((o) => (
              <option key={o.value || 'any'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="bl-admin-users__field">
          <span className="bl-admin-users__field-label">Status</span>
          <select
            value={suspended}
            onChange={(e) =>
              resetToFirstPage(setSuspended)(e.target.value as '' | 'true' | 'false')
            }
          >
            {SUSPENDED_OPTIONS.map((o) => (
              <option key={o.value || 'any'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </form>

      {isError && (
        <p className="bl-admin-users__error" role="alert">
          Could not load users ({error instanceof Error ? error.message : 'unknown error'}).
        </p>
      )}

      {selected.size > 0 && (
        <div className="bl-admin-users__bulk" role="region" aria-label="Bulk actions">
          <span className="bl-admin-users__bulk-count">{selected.size} selected</span>
          <div className="bl-admin-users__bulk-actions">
            <button
              type="button"
              onClick={() => void runBulk('suspend')}
              disabled={bulk.isPending}
              className="bl-admin-users__bulk-btn bl-admin-users__bulk-btn--warn"
            >
              Suspend…
            </button>
            <button
              type="button"
              onClick={() => void runBulk('unsuspend')}
              disabled={bulk.isPending}
              className="bl-admin-users__bulk-btn"
            >
              Unsuspend
            </button>
            <button
              type="button"
              onClick={() => void runBulk('delete')}
              disabled={bulk.isPending}
              className="bl-admin-users__bulk-btn bl-admin-users__bulk-btn--danger"
            >
              Delete…
            </button>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="bl-admin-users__bulk-btn"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {bulk.error && (
        <p className="bl-admin-users__error" role="alert">
          {bulk.error instanceof AuthApiError
            ? bulk.error.detail || bulk.error.message
            : (bulk.error as Error).message}
        </p>
      )}

      {bulkResult && (
        <p className="bl-admin-users__bulk-result" role="status" aria-live="polite">
          {bulkResult.succeeded} succeeded
          {bulkResult.failed > 0 && ` · ${bulkResult.failed} skipped`}.{' '}
          {bulkResult.failed > 0 && (
            <>
              Skipped reasons:{' '}
              {Array.from(
                new Set(
                  bulkResult.rows.filter((r) => !r.ok && r.error).map((r) => r.error as string),
                ),
              ).join(', ')}
            </>
          )}
          <button
            type="button"
            className="bl-admin-users__bulk-result-dismiss"
            onClick={() => setBulkResult(null)}
            aria-label="Dismiss bulk result"
          >
            ×
          </button>
        </p>
      )}

      <div className="bl-admin-users__table-wrap">
        <table className="bl-admin-users__table">
          <thead>
            <tr>
              <th scope="col" className="bl-admin-users__check-col">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = !allSelected && someSelected;
                  }}
                  onChange={toggleAllVisible}
                  aria-label="Select all visible users"
                  disabled={visibleIds.length === 0}
                />
              </th>
              <th scope="col">Email</th>
              <th scope="col">Role</th>
              <th scope="col">Status</th>
              <th scope="col">Last seen</th>
              <th scope="col">Joined</th>
            </tr>
          </thead>
          <tbody>
            {data?.rows.map((row) => (
              <tr key={row.id} data-selected={selected.has(row.id) || undefined}>
                <td className="bl-admin-users__check-col">
                  <input
                    type="checkbox"
                    checked={selected.has(row.id)}
                    onChange={() => toggleOne(row.id)}
                    aria-label={`Select ${row.email}`}
                  />
                </td>
                <td>
                  <Link to={`/admin/users/${row.id}`} className="bl-admin-users__email-link">
                    {row.email || '(no email)'}
                  </Link>
                </td>
                <td>
                  <span className={`bl-admin-users__role bl-admin-users__role--${row.role}`}>
                    {row.role}
                  </span>
                </td>
                <td>
                  {row.suspended_at ? (
                    <span className="bl-admin-users__suspended">Suspended</span>
                  ) : (
                    <span className="bl-admin-users__active">Active</span>
                  )}
                </td>
                <td>{formatRelative(row.last_seen_at)}</td>
                <td>{formatDate(row.created_at)}</td>
              </tr>
            ))}
            {!isLoading && data?.rows.length === 0 && (
              <tr>
                <td colSpan={6} className="bl-admin-users__empty">
                  No users match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <nav className="bl-admin-users__pagination" aria-label="Pagination">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={!canPrev}
        >
          ← Previous
        </button>
        <span className="bl-admin-users__page-label">
          Page {page + 1}
          {total > 0 && ` of ${Math.max(1, Math.ceil(total / PAGE_SIZE))}`}
        </span>
        <button type="button" onClick={() => setPage((p) => p + 1)} disabled={!canNext}>
          Next →
        </button>
      </nav>
    </section>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const ts = new Date(iso).getTime();
  if (!Number.isFinite(ts)) return iso;
  const diffMs = Date.now() - ts;
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(iso);
}
